import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';

// GET /api/surfaces/team/resolutions — the settled record (decision_resolutions STATE rail).
// The durable history behind the Actions "The record" section and the brief's Decision ledger.
// Hand-synced to the MVP contract (ResolutionRecordSchema, lib/dashboard/contracts/team-surfaces.ts).

export interface ResolutionRecord {
  id: string;
  status: 'resolved' | 'disputed' | 'dismissed';
  /** What settled, for display: the typed answer verbatim, else the winning side's snapshot. */
  settledText?: string | null;
  rationale?: string | null;
  topic?: string | null;
  decisionAText?: string | null;
  decisionBText?: string | null;
  authorAName?: string | null;
  authorBName?: string | null;
  conversationA: string;
  conversationB: string;
  resolvedAt: string;
  /** Purge-blind UUID labels server-resolved to display names — null once a member is gone. */
  resolvedByName?: string | null;
  disputedAt?: string | null;
  disputedByName?: string | null;
  disputeNote?: string | null;
  /** Provenance (backend rails 2026-07-27): the caller's own briefing item that surfaced this
   * conflict — null outside the server's bounded lookback or on pre-pair-stamp payloads. */
  surfacedBy?: { briefingItemId: string; briefingDate: string } | null;
}

export interface TeamResolutionsResponse {
  status: 'ready' | 'no_team';
  resolutions: ResolutionRecord[];
  /** Pagination cursor — pass back as ?before= for the next-older page; null at the end. */
  nextBefore?: string | null;
}

export const teamResolutionsQueryKey = ['surfaces', 'team-resolutions'] as const;

export function useTeamResolutions(enabled = true) {
  const query = useQuery({
    queryKey: teamResolutionsQueryKey,
    queryFn: () => apiFetch<TeamResolutionsResponse>('/api/surfaces/team/resolutions'),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
  return { ...query, data: query.data ?? undefined };
}

/**
 * POST /api/surfaces/team/resolutions/:id/dispute — object to a settled record FROM THE LEDGER.
 *
 * This is the record's counterweight to a fast settle: on Actions a conflict commits in one click,
 * which is only safe because the outcome is contestable afterwards. `disputed` is the one status
 * that does NOT suppress the conflict, so objecting brings the card back rather than burying it.
 *
 * State machine, owned by the backend and not to be widened here: only a `resolved` row can be
 * disputed. `dismissed` is terminal and `disputed` is not re-disputable — both come back as a 409,
 * which the UI reports rather than pretending the click worked.
 *
 * Invalidates the brief and Actions payloads too: a reopened conflict belongs back in both.
 */
export function useDisputeResolution() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, disputeNote }: { id: string; disputeNote?: string | null }) => {
      await apiFetch(`/api/surfaces/team/resolutions/${id}/dispute`, {
        method: 'POST',
        body: JSON.stringify({ disputeNote: disputeNote?.trim() || null }),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamResolutionsQueryKey });
      qc.invalidateQueries({ queryKey: ['surfaces', 'team-today'] });
    },
  });
}
